import {useShallow} from 'zustand/react/shallow';
import type {DragCancelEvent, DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import DndContextWrapper, {
  isKeyboardPickup
} from 'client/components/QuestionTypes/shared/DndContextWrapper';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {GuessModelV1} from '@albert-io/models';
import {ItemBankRefsContext} from './ItemBankRefsContext';
import {SupplementChip} from 'client/components/QuestionTypes/shared/DraggableChip';
import {createPortal} from 'react-dom';
import {WORD_BANK_ID} from '../store';
import WordBank from './WordBank.react';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import DropSlots from './DropSlots.react';
import useQuestionStore from 'client/components/QuestionTypes/V2/useQuestionStores';
import LiveRegion from 'lib/LiveRegion';
import {usePrefersReducedMotion} from 'lib/hooks/usePrefersReducedMotion';
import {AnswerFieldset} from '../../shared/AnswerFieldset';
import {useItemAreaCopy} from '../../shared/itemAreaCopy';
import {useItemMoveCopy} from '../../shared/itemMoveCopy';
import {useAnnouncer} from '../../shared/useAnnouncer';
import {useCancelSelectionOnEscape} from '../../shared/useCancelSelectionOnEscape';
import {useRestoreItemFocus} from '../../shared/useRestoreItemFocus';
import {
  backWhereItWasMessage,
  deselectedMessage,
  itemBankAnnouncements,
  placementMessage,
  selectedMessage
} from './announcements';

const ANIMATION_DURATION = 420;
const HIGHLIGHT_DURATION = 900;

interface FlyingChipProps {
  value: string;
  fromRect: DOMRect;
  toRect: DOMRect;
}

const FlyingChip = ({value, fromRect, toRect}: FlyingChipProps) => {
  const dx = toRect.left + toRect.width / 2 - (fromRect.left + fromRect.width / 2);
  const dy = toRect.top + toRect.height / 2 - (fromRect.top + fromRect.height / 2);

  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    top: fromRect.top,
    left: fromRect.left,
    width: fromRect.width,
    pointerEvents: 'none',
    zIndex: 'var(--z-index-popover)',
    margin: 0,
    transform: 'translate(0, 0) scale(1.07)',
    transformOrigin: 'center center',
    transition: 'none'
  });

  useEffect(() => {
    // first frame paints the initial state, second starts the transition
    const frame1 = requestAnimationFrame(() => {
      const frame2 = requestAnimationFrame(() => {
        setStyle((s) => ({
          ...s,
          transition: `transform ${ANIMATION_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          transform: `translate(${dx}px, ${dy}px) scale(1)`
        }));
      });
      return () => cancelAnimationFrame(frame2);
    });
    return () => cancelAnimationFrame(frame1);
  }, [dx, dy]);

  return createPortal(
    <div style={style}>
      <SupplementChip value={value} size='small' />
    </div>,
    document.body
  );
};
interface Props {
  question: Record<string, any>;
  isGuessSubmitted?: boolean;
  isGuessCorrect?: boolean;
  guess?: GuessModelV1 | null;
  // storeName
}

export const CommonBankQuestionArea = ({question, isGuessSubmitted, guess}: Props) => {
  const {initialize, saveDraftGuess, addGuessInfo} = useQuestionAndDrafts(question);
  const useStoreHook = useQuestionStore<'item-bank'>(question);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [flyingItem, setFlyingItem] = useState<FlyingChipProps | null>(null);
  const [highlightedSlotId, setHighlightedSlotId] = useState<string | null>(null);
  const flyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const slotElsRef = useRef<Map<string, HTMLElement>>(new Map());

  const registerItemEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) itemElsRef.current.set(id, el);
    else itemElsRef.current.delete(id);
  }, []);

  const registerSlotEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) slotElsRef.current.set(id, el);
    else slotElsRef.current.delete(id);
  }, []);

  const {
    questionInStore,
    wordBankItems,
    dropSlots,
    placeItemInSlot,
    setActiveItem,
    activeItemId,
    isDragging,
    setIsDragging,
    isSubmitted
    // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  } = useStoreHook(
    useShallow((state) => ({
      questionInStore: state.question,
      wordBankItems: state.wordBank,
      dropSlots: state.dropSlots,
      placeItemInSlot: state.placeItemInSlot,
      activeItemId: state.activeItemId,
      setActiveItem: state.setActiveItem,
      isDragging: state.isDragging,
      setIsDragging: state.setIsDragging,
      isSubmitted: state.isSubmitted
    }))
  );

  const {message, announce} = useAnnouncer();
  const restoreFocusTo = useRestoreItemFocus();
  const copy = useItemMoveCopy();
  const areaCopy = useItemAreaCopy();
  const [dragProgress] = useState({hasLeftOrigin: false});
  const announcements = useMemo(
    () => itemBankAnnouncements(copy, wordBankItems, dropSlots, dragProgress),
    [copy, wordBankItems, dropSlots, dragProgress]
  );

  const onDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    setActiveItem(isKeyboardPickup(event) ? (event.active.id as string) : null);
  };

  const onDrop = (event: DragEndEvent) => {
    const {active, over} = event;
    setIsDragging(false);
    setActiveItem(null);
    restoreFocusTo(active.id as string);

    if (!over || active.id === over.id) return;
    placeItemInSlot(active.id as string, over.id as string);
    saveDraftGuess();
  };

  const onDragCancel = (_event: DragCancelEvent, {tabbedAway}: {tabbedAway: boolean}) => {
    setIsDragging(false);
    if (!tabbedAway) setActiveItem(null);
  };

  const handleItemClick = useCallback(
    (itemId: string) => {
      if (isSubmitted) return;
      // Don't process clicks during drag operations
      if (isDragging) return;

      const clickedItem = wordBankItems.find((item) => item.id === itemId);
      if (!clickedItem) {
        setActiveItem(null);
        return;
      }

      // If clicking the already-selected item, deselect it
      if (activeItemId === itemId) {
        setActiveItem(null);
        announce(deselectedMessage(copy, clickedItem));
      } else if (activeItemId) {
        // If another item is selected, move the active item to where the clicked item is
        // The clicked item will be sent back to the word bank
        const targetId = clickedItem.placedInSlot || WORD_BANK_ID;
        announce(placementMessage(copy, wordBankItems, dropSlots, activeItemId, targetId));
        placeItemInSlot(activeItemId, targetId);
        saveDraftGuess();
        setActiveItem(null);
        restoreFocusTo(activeItemId);
      } else {
        // Select this item for click-to-move
        setActiveItem(itemId);
        announce(selectedMessage(copy, clickedItem));
      }
    },
    [
      wordBankItems,
      dropSlots,
      setActiveItem,
      isDragging,
      placeItemInSlot,
      activeItemId,
      saveDraftGuess,
      isSubmitted,
      announce,
      copy,
      restoreFocusTo
    ]
  );

  const handleSlotClick = useCallback(
    (slotId: string) => {
      if (isSubmitted) return;
      if (isDragging) return;

      if (!activeItemId) return;

      // Measure before committing so the fly starts from the item's current on-screen spot.
      const sourceEl = itemElsRef.current.get(activeItemId) ?? null;
      const destEl = slotElsRef.current.get(slotId) ?? null;
      const activeItem = wordBankItems.find((item) => item.id === activeItemId);
      const fly =
        sourceEl && destEl && activeItem
          ? {
              value: activeItem.value,
              fromRect: sourceEl.getBoundingClientRect(),
              toRect: destEl.getBoundingClientRect()
            }
          : null;

      // Commit the placement immediately so a submit or navigation during the animation
      // reflects what the student sees; the fly is a cosmetic flourish over the placed item.
      announce(placementMessage(copy, wordBankItems, dropSlots, activeItemId, slotId));
      placeItemInSlot(activeItemId, slotId);
      setActiveItem(null);
      saveDraftGuess();
      restoreFocusTo(activeItemId);

      if (fly) {
        if (flyTimeoutRef.current) clearTimeout(flyTimeoutRef.current);
        if (prefersReducedMotion) {
          setHighlightedSlotId(slotId);
        } else {
          setFlyingItem(fly);
        }
        flyTimeoutRef.current = setTimeout(
          () => {
            setFlyingItem(null);
            setHighlightedSlotId(null);
            flyTimeoutRef.current = null;
          },
          prefersReducedMotion ? HIGHLIGHT_DURATION : ANIMATION_DURATION
        );
      }
    },
    [
      activeItemId,
      wordBankItems,
      dropSlots,
      placeItemInSlot,
      setActiveItem,
      isDragging,
      saveDraftGuess,
      isSubmitted,
      announce,
      copy,
      restoreFocusTo,
      prefersReducedMotion
    ]
  );

  const handleBankClick = useCallback(() => {
    handleSlotClick(WORD_BANK_ID);
  }, [handleSlotClick]);

  // Reset (unplace) an item back to the word bank
  const handleResetItem = useCallback(
    (itemId: string) => {
      if (isSubmitted) return;
      announce(placementMessage(copy, wordBankItems, dropSlots, itemId, WORD_BANK_ID));
      placeItemInSlot(itemId, WORD_BANK_ID);
      setActiveItem(null);
      saveDraftGuess();
      restoreFocusTo(itemId);
    },
    [
      isSubmitted,
      wordBankItems,
      dropSlots,
      placeItemInSlot,
      setActiveItem,
      saveDraftGuess,
      announce,
      copy,
      restoreFocusTo
    ]
  );

  useCancelSelectionOnEscape(activeItemId !== null && !isDragging, () => {
    const selected = wordBankItems.find((item) => item.id === activeItemId);
    setActiveItem(null);
    if (!selected) return;
    announce(backWhereItWasMessage(copy, selected));
    restoreFocusTo(selected.id);
  });

  useEffect(() => {
    if (question && question.size > 0) {
      if (isGuessSubmitted && guess) {
        addGuessInfo(guess);
      } else {
        // Explicitly clear any stale submission state from a previously viewed question
        addGuessInfo(null);
      }
      initialize(question);
    }
  }, [question, initialize, isGuessSubmitted, guess, addGuessInfo]);

  useEffect(
    () => () => {
      if (flyTimeoutRef.current) clearTimeout(flyTimeoutRef.current);
    },
    []
  );

  if (!questionInStore) return null;

  return (
    <ItemBankRefsContext.Provider value={{registerItemEl, registerSlotEl}}>
      <DndContextWrapper
        keyboardSelects
        onDragStart={onDragStart}
        onDragEnd={onDrop}
        onDragCancel={onDragCancel}
        renderOverlay={(activeId) => {
          const item = wordBankItems.find((i) => i.id === activeId);
          return item ? <SupplementChip value={item.value} size='small' /> : null;
        }}
        accessibility={{announcements}}
      >
        {flyingItem && <FlyingChip {...flyingItem} />}
        <div className='mb-x4'>
          <AnswerFieldset instruction={areaCopy.itemBankInstruction} isSubmitted={isSubmitted}>
            <WordBank
              handleItemClick={handleItemClick}
              question={question}
              handleBankClick={handleBankClick}
            />
            <DropSlots
              question={question}
              handleItemClick={handleItemClick}
              handleSlotClick={handleSlotClick}
              handleResetItem={handleResetItem}
              highlightedSlotId={highlightedSlotId}
            />
          </AnswerFieldset>
        </div>
      </DndContextWrapper>
      <LiveRegion assertive={false} className='sr-only'>
        {message}
      </LiveRegion>
    </ItemBankRefsContext.Provider>
  );
};

export default CommonBankQuestionArea;
